import type { TunerExport, TunerNote } from '../store/tuningStore'
import { computeCentsScale } from './noteUtils'

export function exportTunerImage(tunerData: TunerExport): void {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      console.error('Failed to get canvas context')
      return
    }

    if (!tunerData || !tunerData.notes || tunerData.notes.length === 0) {
      console.error('No notes to export')
      alert('Aucune note à exporter')
      return
    }

    const width = 900
    const padding = 40
    const headerHeight = 110
    const footerHeight = 60
    const rowHeight = 52

    const notes = tunerData.notes // already ordered low to high pitch
    const height = headerHeight + notes.length * rowHeight + footerHeight

    canvas.width = width
    canvas.height = height

    const bgColor = '#FFFFFF'
    const textColor = '#000000'
    const mutedColor = '#666666'
    const trackColor = '#E5E5E5'
    const tonicBg = '#F3F0FF'
    const positiveColor = '#2563EB'
    const negativeColor = '#DC2626'
    const neutralColor = '#666666'

    ctx.fillStyle = bgColor
    ctx.fillRect(0, 0, width, height)

    // Title
    ctx.fillStyle = textColor
    ctx.font = 'bold 30px Arial'
    ctx.textAlign = 'center'
    ctx.fillText(`Tuner: ${tunerData.tonic.name} = ${tunerData.tonic.frequency.toFixed(2)} Hz`, width / 2, 48)

    if (tunerData.presetName) {
      ctx.font = '16px Arial'
      ctx.fillStyle = mutedColor
      ctx.fillText(tunerData.presetName, width / 2, 74)
    }

    // Column layout
    const contentX = padding
    const contentWidth = width - padding * 2
    const nameColW = 130
    const centsColW = 90
    const freqColW = 110
    const trackX = contentX + nameColW
    const trackW = contentWidth - nameColW - centsColW - freqColW
    const centsX = trackX + trackW
    const freqX = centsX + centsColW

    const scale = computeCentsScale(notes.map((n) => n.cents))
    const zeroX = trackX + ((0 - scale.min) / (scale.max - scale.min)) * trackW

    notes.forEach((note: TunerNote, index: number) => {
      const rowY = headerHeight + index * rowHeight
      const centerY = rowY + rowHeight / 2

      if (note.isTonic) {
        ctx.fillStyle = tonicBg
        ctx.fillRect(contentX - 12, rowY, contentWidth + 24, rowHeight)
      }

      // Note name + english name + tag
      ctx.textAlign = 'left'
      ctx.fillStyle = textColor
      ctx.font = 'bold 20px Arial'
      ctx.fillText(note.name, contentX, centerY - 4)
      ctx.font = '12px Arial'
      ctx.fillStyle = mutedColor
      ctx.fillText(note.nameEn, contentX, centerY + 14)

      if (note.isTonic || note.isExtra) {
        const tagText = note.isTonic ? 'tonique' : 'ajoutée'
        ctx.font = '11px Arial'
        const tagWidth = ctx.measureText(tagText).width + 14
        const tagX = contentX + 62
        ctx.fillStyle = note.isTonic ? '#EDE9FE' : '#F0F0F0'
        ctx.beginPath()
        ctx.roundRect(tagX, centerY - 20, tagWidth, 20, 10)
        ctx.fill()
        ctx.fillStyle = note.isTonic ? '#6D28D9' : '#666666'
        ctx.textAlign = 'left'
        ctx.fillText(tagText, tagX + 7, centerY - 6)
      }

      // Track
      ctx.strokeStyle = trackColor
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(trackX, centerY)
      ctx.lineTo(trackX + trackW, centerY)
      ctx.stroke()

      // Zero tick
      ctx.strokeStyle = '#BBBBBB'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.moveTo(zeroX, centerY - 9)
      ctx.lineTo(zeroX, centerY + 9)
      ctx.stroke()

      // Dot
      const dotX = trackX + ((note.cents - scale.min) / (scale.max - scale.min)) * trackW
      const dotColor = note.cents > 0 ? positiveColor : note.cents < 0 ? negativeColor : neutralColor
      ctx.fillStyle = dotColor
      ctx.beginPath()
      ctx.arc(dotX, centerY, 6, 0, Math.PI * 2)
      ctx.fill()

      // Cents text
      const centsText = note.cents === 0 ? '0¢' : note.cents > 0 ? `+${note.cents.toFixed(1)}¢` : `${note.cents.toFixed(1)}¢`
      ctx.font = 'bold 16px Arial'
      ctx.fillStyle = dotColor
      ctx.textAlign = 'right'
      ctx.fillText(centsText, centsX + centsColW - 8, centerY + 5)

      // Frequency
      ctx.font = '13px Arial'
      ctx.fillStyle = mutedColor
      ctx.textAlign = 'right'
      ctx.fillText(`${note.frequency.toFixed(1)} Hz`, freqX + freqColW - 8, centerY + 5)

      // Row separator
      if (index < notes.length - 1) {
        ctx.strokeStyle = '#EEEEEE'
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(contentX - 12, rowY + rowHeight)
        ctx.lineTo(contentX + contentWidth + 12, rowY + rowHeight)
        ctx.stroke()
      }
    })

    // Footer date
    ctx.font = '14px Arial'
    ctx.fillStyle = mutedColor
    ctx.textAlign = 'center'
    const date = new Date(tunerData.date).toLocaleDateString('fr-FR')
    ctx.fillText(date, width / 2, height - 22)

    canvas.toBlob((blob) => {
      if (!blob) {
        console.error('Failed to create blob from canvas')
        alert('Erreur lors de la création de l\'image')
        return
      }

      try {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        let fileName: string
        if (tunerData.presetName) {
          const cleanPresetName = tunerData.presetName.replace(/[^a-zA-Z0-9_-]/g, '_')
          fileName = `tuner_${cleanPresetName}.png`
        } else {
          fileName = `tuner_${tunerData.tonic.nameEn}_${tunerData.tonic.frequency.toFixed(2)}Hz.png`
        }
        a.download = fileName
        a.style.display = 'none'

        document.body.appendChild(a)
        a.click()

        setTimeout(() => {
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }, 100)
      } catch (error) {
        console.error('Error downloading image:', error)
        alert('Erreur lors du téléchargement de l\'image')
      }
    }, 'image/png')
  } catch (error) {
    console.error('Error exporting tuner image:', error)
    alert('Erreur lors de l\'export de l\'image')
  }
}
